import { FC, useEffect, useState } from "react";
import "./CqGenerationShimmer.scss";

const TOTAL_SECONDS = 30;

// Staged copy shown while the backend queue worker generates competency questions from the
// initiative's business questions (mirrors SearchShimmer's stage-progression pattern).
const STAGES = [
  { label: "Reviewing business questions…", icon: "🔍", duration: 5 },
  { label: "Mapping ontology context…", icon: "📚", duration: 6 },
  { label: "Generating competency questions…", icon: "⚙️", duration: 7 },
  { label: "Scoring confidence & traceability…", icon: "📊", duration: 6 },
  { label: "Finalizing competency question set…", icon: "✨", duration: 6 }
];

const CqGenerationShimmer: FC = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsElapsed((prev) =>
        prev < TOTAL_SECONDS ? prev + 1 : TOTAL_SECONDS
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cumulative = 0;
    for (let i = 0; i < STAGES.length; i++) {
      cumulative += STAGES[i].duration;
      if (secondsElapsed < cumulative) {
        setActiveStage(i);
        return;
      }
    }
    setActiveStage(STAGES.length - 1);
  }, [secondsElapsed]);

  const progressPercent = (secondsElapsed / TOTAL_SECONDS) * 100;

  return (
    <div className="cq-generation-shimmer">
      <div className="cq-generation-shimmer__card">
        <div className="cq-generation-shimmer__header">
          <div className="cq-generation-shimmer__header-left">
            <div className="cq-generation-shimmer__pulse-ring">
              <span className="cq-generation-shimmer__status-dot" />
            </div>
            <div>
              <h2 className="cq-generation-shimmer__heading">
                Generating Competency Questions
              </h2>
              <p className="cq-generation-shimmer__subtitle">
                Analyzing your business questions to generate ontology-aligned
                competency questions.
              </p>
            </div>
          </div>
        </div>

        <div className="cq-generation-shimmer__progress">
          <div
            className="cq-generation-shimmer__progress-fill"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>

        <div className="cq-generation-shimmer__steps">
          {STAGES.map((stage, i) => {
            const isDone = i < activeStage;
            const isActive = i === activeStage;
            return (
              <div
                key={i}
                className={`cq-generation-shimmer__step ${
                  isDone
                    ? "cq-generation-shimmer__step--done"
                    : isActive
                    ? "cq-generation-shimmer__step--active"
                    : ""
                }`}
              >
                <div className="cq-generation-shimmer__step-icon">
                  {isDone ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <defs>
                        <linearGradient
                          id="cqGenerationCheckGrad"
                          x1="0"
                          y1="0"
                          x2="20"
                          y2="20"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#4F7BF7" />
                          <stop offset="100%" stopColor="#E84FAD" />
                        </linearGradient>
                      </defs>
                      <circle
                        cx="10"
                        cy="10"
                        r="10"
                        fill="url(#cqGenerationCheckGrad)"
                      />
                      <path
                        d="M6 10L9 13L14 7"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="cq-generation-shimmer__step-emoji">
                      {stage.icon}
                    </span>
                  )}
                </div>
                <div className="cq-generation-shimmer__step-content">
                  <span className="cq-generation-shimmer__step-label">
                    {stage.label}
                  </span>
                  {isActive && (
                    <div className="cq-generation-shimmer__step-bar">
                      <div className="cq-generation-shimmer__step-bar-fill" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CqGenerationShimmer;
